import {StyleSheet} from 'aphrodite';
import assert from 'assert';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartNodeDefinition,
  FlowchartNodeInfo,
  FlowchartNodeInstanceData,
  FlowchartNodeShape,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {NodeStyles} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  CHARACTER_THRESHOLD_FOR_WIDE_FLOWCHART_NODE,
  NODE_NAME_HEIGHT,
  ProcessFlowchartNodeEdgeLength,
  ProcessFlowchartNodeHeight,
  ProcessFlowchartNodeWidth,
  ProcessFlowchartNodeDataType,
  ProcessFlowchartUIOnlyNodeType,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {GRAY_DARKER, NEAR_BLACK, GRAY} from 'ui-library/Colors';

const errorIconStyles = StyleSheet.create({
  errorIconTopLeft: {
    position: 'absolute',
    top: '4px',
    left: '6px',
  },
  errorIconCenter: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const actionFunctionStyles = StyleSheet.create({
  actionFunctionToolbarDefault: {
    marginTop: -15,
    marginLeft: -5,
  },
  actionFunctionToolbarRouterNode: {
    marginTop: -5,
    marginLeft: -25,
  },
  actionFunctionToolbarEndNode: {
    marginTop: -10,
    marginLeft: -10,
  },
});

const nodeShapeStyles = StyleSheet.create({
  ovalNode: {
    borderRadius: '50px',
    padding: '8px 24px',
    height: ProcessFlowchartNodeHeight.DEFAULT,
  },
  rectangleNode: {
    width: '100%',
    borderRadius: 4,
    height: ProcessFlowchartNodeHeight.DEFAULT,
  },
  diamondNode: {
    width: ProcessFlowchartNodeEdgeLength.DIAMOND,
    height: ProcessFlowchartNodeEdgeLength.DIAMOND,
    minWidth: 'auto',
    transform: 'translatey(25%) rotate(-45deg)',
  },
  pillNode: {
    width: ProcessFlowchartNodeWidth.PILL,
    height: ProcessFlowchartNodeHeight.PILL,
    minWidth: 'auto',
    borderRadius: '32px',
  },
  circleNodeWithDarkBackground: {
    width: ProcessFlowchartNodeWidth.CIRCLE,
    height: ProcessFlowchartNodeHeight.CIRCLE,
    minWidth: 'auto',
    borderRadius: '50%',
    background: GRAY,
    justifyContent: 'center',
    padding: 0,
  },
});

const nameTextStyles = StyleSheet.create({
  floatingNodeName: {
    color: GRAY_DARKER,
  },
  innerNodeName: {
    color: NEAR_BLACK,
    paddingBottom: 4,
  },
});

export const RECTANGLE_NODE_STYLES: NodeStyles = {
  errorStyle: errorIconStyles.errorIconTopLeft,
  shapeStyle: nodeShapeStyles.rectangleNode,
  actionFunctionStyle: actionFunctionStyles.actionFunctionToolbarDefault,
  nameStyle: nameTextStyles.innerNodeName,
};

export const DIAMOND_NODE_STYLES: NodeStyles = {
  errorStyle: errorIconStyles.errorIconCenter,
  shapeStyle: nodeShapeStyles.diamondNode,
  actionFunctionStyle: actionFunctionStyles.actionFunctionToolbarRouterNode,
  nameStyle: nameTextStyles.floatingNodeName,
};

export const OVAL_NODE_STYLES: NodeStyles = {
  errorStyle: errorIconStyles.errorIconTopLeft,
  shapeStyle: nodeShapeStyles.ovalNode,
  actionFunctionStyle: actionFunctionStyles.actionFunctionToolbarDefault,
  nameStyle: nameTextStyles.innerNodeName,
};

export const PILL_NODE_STYLES: NodeStyles = {
  errorStyle: errorIconStyles.errorIconTopLeft,
  shapeStyle: nodeShapeStyles.pillNode,
  actionFunctionStyle: actionFunctionStyles.actionFunctionToolbarEndNode,
  nameStyle: nameTextStyles.floatingNodeName,
};

export const CIRCLE_NODE_STYLES: NodeStyles = {
  errorStyle: null,
  shapeStyle: nodeShapeStyles.circleNodeWithDarkBackground,
  actionFunctionStyle: null,
  nameStyle: null,
};

export const TASK_NODE_INFO: FlowchartNodeInfo = {
  nodeDefinition: {
    nodeStyles: RECTANGLE_NODE_STYLES,
    dropKey: '_task',
  },
  instanceData: {
    shape: FlowchartNodeShape.RECTANGLE,
    height: ProcessFlowchartNodeHeight.DEFAULT,
    width: ProcessFlowchartNodeWidth.DEFAULT,
  },
};

export const ROOT_NODE_INFO: FlowchartNodeInfo = {
  nodeDefinition: {
    nodeStyles: OVAL_NODE_STYLES,
    dropKey: '_root',
  },
  instanceData: {
    shape: FlowchartNodeShape.OVAL,
    height: ProcessFlowchartNodeHeight.DEFAULT,
    width: ProcessFlowchartNodeWidth.DEFAULT,
  },
};

export const OUTPUT_NODE_INFO: FlowchartNodeInfo = {
  nodeDefinition: {
    nodeStyles: OVAL_NODE_STYLES,
    dropKey: '_output',
  },
  instanceData: {
    shape: FlowchartNodeShape.OVAL,
    height: ProcessFlowchartNodeHeight.DEFAULT,
    width: ProcessFlowchartNodeWidth.DEFAULT,
  },
};

export const ROUTER_NODE_INFO: FlowchartNodeInfo = {
  nodeDefinition: {
    nodeStyles: DIAMOND_NODE_STYLES,
    dropKey: '_router',
  },
  instanceData: {
    shape: FlowchartNodeShape.DIAMOND,
    height: ProcessFlowchartNodeHeight.DIAMOND,
    width: ProcessFlowchartNodeWidth.DIAMOND,
    nameHeight: NODE_NAME_HEIGHT,
  },
};

export const END_NODE_INFO: FlowchartNodeInfo = {
  nodeDefinition: {
    nodeStyles: PILL_NODE_STYLES,
    dropKey: '_end',
  },
  instanceData: {
    shape: FlowchartNodeShape.PILL,
    height: ProcessFlowchartNodeHeight.PILL,
    width: ProcessFlowchartNodeWidth.PILL,
    nameHeight: NODE_NAME_HEIGHT,
  },
};

export const COLLAPSED_NODE_INFO: FlowchartNodeInfo = {
  nodeDefinition: {
    nodeStyles: CIRCLE_NODE_STYLES,
    dropKey: '_collapsed_node',
  },
  instanceData: {
    shape: FlowchartNodeShape.CIRCLE,
    height: ProcessFlowchartNodeHeight.CIRCLE,
    width: ProcessFlowchartNodeWidth.CIRCLE,
  },
};

const NODE_TYPE_TO_INFO: Record<ProcessFlowchartNodeDataType, FlowchartNodeInfo> = {
  [ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS]: TASK_NODE_INFO,
  [ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS]: ROUTER_NODE_INFO,
  [ProcessFlowchartNodeDetailsType.END_NODE_DETAILS]: END_NODE_INFO,
  [ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS]: ROOT_NODE_INFO,
  [ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS]: OUTPUT_NODE_INFO,
  [ProcessFlowchartUIOnlyNodeType.COLLAPSED_NODE]: COLLAPSED_NODE_INFO,
};

export function getNodeDefinition(type: ProcessFlowchartNodeDataType): FlowchartNodeDefinition {
  return NODE_TYPE_TO_INFO[type].nodeDefinition;
}

export function getNodeInstanceData(
  type: ProcessFlowchartNodeDataType,
  name: string
): FlowchartNodeInstanceData {
  const instanceData = NODE_TYPE_TO_INFO[type].instanceData;

  const FIXED_WIDTH_NODES = [
    ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
    ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
    ProcessFlowchartUIOnlyNodeType.COLLAPSED_NODE,
  ];
  const DYNAMIC_WIDTH_NODES: Array<ProcessFlowchartNodeDataType> = [
    ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
    ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
    ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
  ];

  if (FIXED_WIDTH_NODES.includes(type)) {
    return instanceData;
  }

  assert(DYNAMIC_WIDTH_NODES.includes(type), `Unsupported node type ${type}`);

  if (name.length > CHARACTER_THRESHOLD_FOR_WIDE_FLOWCHART_NODE) {
    return {
      ...instanceData,
      width: ProcessFlowchartNodeWidth.WIDE,
    };
  }

  return instanceData;
}
